import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { Link } from 'react-router-dom';
import { Bell, Check, Trash, ArrowRight, Warning, UserCheck } from '@phosphor-icons/react';

const VELD_LABEL = {
  naam: 'Naam wijziging',
  adres: 'Adres wijziging',
  actief: 'Status wijziging',
};

const VELD_ICOON = (v) => v === 'actief'
  ? <Warning size={18} weight="fill" className="text-red-500" />
  : v === 'adres'
    ? <ArrowRight size={18} weight="bold" className="text-[#E5B300]" />
    : <UserCheck size={18} weight="bold" className="text-[#002FA7]" />;

export default function NotificatiesPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [checkBusy, setCheckBusy] = useState(false);
  const [geblokkeerd, setGeblokkeerd] = useState([]);

  const load = () => {
    API.get('/notificaties', { params: { filter } }).then(r => setItems(r.data.items || []));
    API.get('/klanten/geblokkeerd-vies').then(r => setGeblokkeerd(r.data.items || [])).catch(() => setGeblokkeerd([]));
  };
  useEffect(() => { load(); }, [filter]);

  const markGelezen = async (id) => {
    await API.post(`/notificaties/${id}/markeer-gelezen`);
    load();
  };
  const markAllGelezen = async () => {
    await API.post('/notificaties/markeer-alle-gelezen');
    load();
  };
  const remove = async (id) => {
    if (!window.confirm('Verwijder deze notificatie?')) return;
    await API.delete(`/notificaties/${id}`);
    load();
  };

  const checkAlle = async () => {
    setCheckBusy(true);
    try {
      const r = await API.post('/klanten/check-alle');
      alert(`${r.data.gecontroleerd} klanten gecontroleerd — ${r.data.wijzigingen} nieuwe wijzigingen gevonden.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Controle mislukt');
    } finally {
      setCheckBusy(false);
    }
  };

  const unread = items.filter(i => !i.gelezen).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="notificaties-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell size={28} weight="bold" className="text-[#002FA7]" />
            {unread > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Notificaties</h1>
            <p className="text-xs text-gray-500 mt-0.5">Wijzigingen in klantengegevens gedetecteerd via wekelijkse VIES-controle</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn btn-ghost btn-sm inline-flex items-center gap-1.5"
            onClick={checkAlle}
            disabled={checkBusy}
            data-testid="check-alle-btn"
          >
            {checkBusy ? 'Bezig...' : 'Controleer nu alle klanten'}
          </button>
          {unread > 0 && (
            <button
              className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
              onClick={markAllGelezen}
              data-testid="mark-all-btn"
            >
              <Check size={14} /> Alles als gelezen markeren
            </button>
          )}
        </div>
      </div>

      {/* Geblokkeerde klanten overzicht (altijd bovenaan indien aanwezig) */}
      {geblokkeerd.length > 0 && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4" data-testid="geblokkeerd-panel">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={20} weight="fill" className="text-red-600" />
            <h2 className="text-base font-bold text-red-900">Facturatie geblokkeerd — {geblokkeerd.length} klant{geblokkeerd.length === 1 ? '' : 'en'}</h2>
          </div>
          <p className="text-xs text-red-800 mb-3">Voor deze klanten kan je geen nieuwe documenten aanmaken of bestaande inhoudelijk wijzigen. Bevestig de VIES-wijziging om de blokkering op te heffen.</p>
          <div className="space-y-2">
            {geblokkeerd.map(g => (
              <div key={g.klant_id} className="flex items-center gap-3 px-3 py-2 bg-white border border-red-200 text-xs" data-testid={`geblokkeerd-${g.klant_id}`}>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-red-600 text-white">GEBLOKKEERD</span>
                <Link to="/klanten" className="font-bold text-gray-900 hover:text-[#002FA7] hover:underline">{g.klant_naam}</Link>
                <span className="text-gray-400 font-mono">{g.btwnr}</span>
                <span className="text-gray-500 line-through text-[11px]">{g.oud}</span>
                <span>→</span>
                <span className="font-semibold text-red-700">{g.nieuw}</span>
                <span className="ml-auto text-[10px] text-gray-400">{new Date(g.tijdstip).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                <button
                  className="text-[10px] font-bold px-2 py-1 bg-[#002FA7] text-white hover:bg-[#001F6F]"
                  onClick={async () => {
                    if (!window.confirm(`Wijziging bevestigen en blokkering opheffen voor ${g.klant_naam}?`)) return;
                    await API.post(`/notificaties/${g.alert_id}/markeer-gelezen`);
                    load();
                  }}
                  data-testid={`deblokkeer-${g.klant_id}`}
                >
                  Bevestig &amp; deblokkeer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-4">
        {[
          { k: 'all', label: 'Alle', count: items.length },
          { k: 'unread', label: 'Ongelezen', count: unread },
        ].map(t => (
          <button
            key={t.k}
            className={`py-2 px-1 text-sm font-semibold transition-colors ${filter === t.k ? 'text-[#002FA7] border-b-2 border-[#002FA7] -mb-[1px]' : 'text-gray-500 hover:text-gray-800'}`}
            onClick={() => setFilter(t.k)}
            data-testid={`tab-${t.k}`}
          >
            {t.label} <span className="text-xs text-gray-400 ml-1">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Lijst */}
      {items.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Bell size={40} weight="thin" className="mx-auto mb-3 opacity-40" />
          <div className="text-sm">Geen notificaties.</div>
          <div className="text-xs mt-1">De eerstvolgende automatische controle is elke maandag om 06:00.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {items.map(n => (
            <div
              key={n.id}
              className={`flex items-start gap-3 p-4 border transition-colors ${n.gelezen ? 'bg-white border-gray-200' : 'bg-[#F0F4FF] border-[#002FA7]/30'} ${n.kritiek ? 'border-l-4 border-l-red-500' : ''}`}
              data-testid={`notif-${n.id}`}
            >
              <div className="shrink-0 mt-0.5">{VELD_ICOON(n.veld)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${n.kritiek ? 'bg-red-100 text-red-700' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
                    {VELD_LABEL[n.veld] || n.veld}
                  </span>
                  <Link to={`/klanten`} className="text-sm font-bold text-gray-900 hover:text-[#002FA7] hover:underline truncate" data-testid={`notif-klant-${n.id}`}>
                    {n.klant_naam}
                  </Link>
                  <span className="text-[11px] text-gray-400 font-mono">{n.btwnr}</span>
                  <span className="ml-auto text-[10px] text-gray-400 shrink-0">{new Date(n.tijdstip).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
                <div className="text-xs space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 shrink-0 w-12">Oud:</span>
                    <span className="text-gray-500 line-through">{n.oud || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 shrink-0 w-12">Nieuw:</span>
                    <span className={`font-semibold ${n.kritiek ? 'text-red-700' : 'text-gray-900'}`}>{n.nieuw}</span>
                  </div>
                </div>
              </div>
              <div className="shrink-0 flex flex-col gap-1">
                {!n.gelezen && (
                  <button
                    className="text-gray-400 hover:text-[#008A27] p-1"
                    onClick={() => markGelezen(n.id)}
                    title="Markeer als gelezen"
                    data-testid={`mark-${n.id}`}
                  >
                    <Check size={16} />
                  </button>
                )}
                <button
                  className="text-gray-400 hover:text-red-500 p-1"
                  onClick={() => remove(n.id)}
                  title="Verwijderen"
                  data-testid={`del-${n.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
